import isIframe from './helpers/is-iframe'
import type { WidgetEventData, MessageData, ApiEventType, ApiErrorEvent, WorkflowExecutionPhase } from './types'
import useWidgetHooks from '@/composables/widget-hooks/useWidgetHooks'
import { createOrchestratorApi } from '@/composables/orchestrator/useOrchestratorApi'
import { createAppsApi } from '@/composables/apps-api/useAppsApi'
import useWidgetOptions from '@/composables/widget-options/useWidgetOptions'
import useWidgetViewManager from '@/composables/widget-view-manager/useWidgetViewManager'
import { WidgetViews } from '@/types'
import type { WidgetOptions } from '@/composables/widget-options/useWidgetOptions'
import { until, useEventBus } from '@vueuse/core'
import { logger } from '@sentry/vue'
import { pick } from 'ramda'
import { v4 as uuid } from 'uuid'
import { settleParentRequest, waitForParentResult } from './functions/parent-requests'

const handleParentMessage = async (message: MessageEvent<MessageData>) => {
    const { preInitialize, initialize, resetInitialization, reinitialize, initializeCustomPagePreview, initialized } = useWidgetHooks()
    const { setOptions } = useWidgetOptions()
    const { changeView } = useWidgetViewManager()

    const { type, data } = message.data

    logger.trace(`Received ${type} message from parent`)


    // deprecated
    if (type === 'widget.setContext') {
        await preInitialize(data.token, data.options, data.clientDomain)
    }

    if (type === 'widget.open' || type === 'widget.initialize') {
        emitEventToApi('widget-opened')
        initialize()
    }

    if (type === 'widget.close') {
        resetInitialization()
        if (data.emitEvent) {
            emitEventToApi('widget-closed')
        }
    }

    if (type === 'widget.reinitialize') {
        reinitialize()
    }

    if (type === 'widget.setOptions') {
        const { token, clientDomain, ...otherOptions } = data
        if (token) {
            await preInitialize(token, otherOptions, clientDomain)
        } else {
            setOptions(otherOptions)
        }
    }

    if (type === 'widget.showCustomPagePreview') {
        setOptions(pick(['widgetOptions'], JSON.parse(data)).widgetOptions as WidgetOptions)

        initializeCustomPagePreview()

        changeView(WidgetViews.CUSTOM_PAGE_PREVIEW, {
            customPagePreview: true,
            customPagePreviewData: data,
        })
    }

    if (type === 'widget.updateCustomPagePreview') {
        useEventBus('update-custom-page-preview').emit(data)
    }

    if (type === 'widget.customPagePreviewValuesResult') {
        settleParentRequest(data)
    }

    if (type === 'widget.apiEventHandlerAdded') {
        if (!initialized.value) {
            await until(initialized).toBe(true)
        }
    }
}

const postMessageToWrapper = (message: MessageData) => {
    if (!isIframe) return
    window.parent.postMessage(message, '*')
}

const initializeIframe = () => {
    window.addEventListener('message', handleParentMessage)

    setWorkflowExecutionPhase('init')

    createOrchestratorApi()

    createAppsApi()
    postMessageToWrapper({
        type: 'widget-wrapper.ready',
    })
}

const closeWidget = (emitEvent = true) => {
    const { options } = useWidgetOptions()
    postMessageToWrapper({
        type: 'widget-wrapper.close',
        data: {
            autoCloseWidget: options.value.autoCloseWidget,
            emitEvent,
        },
    })
}

const finishWorkflow = (data: WidgetEventData = null) => {
    emitEventToApi('workflow-finished', data)
}

const emitErrorEvent = (details: ApiErrorEvent) => {
    // Timeout gives us a chance to report the issue to Sentry before emitting the error event
    setTimeout(() => {
        emitEventToApi('error', details)
    }, 1000)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const emitEventToApi = (event: ApiEventType, details?: any) => {
    logger.trace(`Emit ${event} event to parent`, details)

    postMessageToWrapper({
        type: 'widget-wrapper.api-event',
        data: {
            event,
            details,
        },
    })
}

// for work only the parent can do (e.g. authorized calls); the parent answers with the same requestId
const requestFromParent = <T>(event: ApiEventType, details: Record<string, unknown> = {}) => {
    const requestId = uuid()
    const result = waitForParentResult<T>(requestId)

    emitEventToApi(event, { ...details, requestId })

    return result
}

const openDeeplink = (link: string) => {
    postMessageToWrapper({
        type: 'widget-wrapper.open-deeplink',
        data: {
            link,
        },
    })
}

const setIframeHeight = (data: string) => {
    postMessageToWrapper({
        type: 'widget-wrapper.set-height',
        data,
    })
}

const setWrapperOptions = (data: Partial<WidgetOptions>) => {
    postMessageToWrapper({
        type: 'widget-wrapper.set-wrapper-options',
        data: JSON.stringify(data),
    })
}

const setWorkflowExecutionPhase = (data: WorkflowExecutionPhase) => {
    postMessageToWrapper({
        type: 'widget-wrapper.set-workflow-execution-phase',
        data,
    })
}

function useEmbed() {
    return {
        closeWidget,
        initializeIframe,
        finishWorkflow,
        emitEventToApi,
        requestFromParent,
        openDeeplink,
        emitErrorEvent,
        setIframeHeight,
        setWrapperOptions,
        setWorkflowExecutionPhase,
    }
}

export default useEmbed
